import { useMemo } from 'react';
import { isEmpty } from 'lodash';
import { nanoid as generate } from 'nanoid';
import { AnalysisStatisticAggregationType, PlotNumericColumnsV2 } from 'api/data/analyses';
import { Chart } from 'components/UI/Chart';
import { ScalesLabels } from 'types/index';
import { ZingChartClickEvent } from 'zingchart-react';
import { AnalysisContainer } from '../../UI';
import { NoPlot } from '../../NoPlot';
import { useComputePlotNumericColumnsChartDataV2 as useComputeData } from 'helpers/analysis';
import { withMemo } from 'helpers/HOCs';

import {
	useTranslation,
	useAnalysisContext,
	useToggleAnalysisChartPlot,
	useFullscreenAnalysis,
	useAnalysesActiveColum,
	useAnalyses,
	useFilters
} from 'hooks/store';
import { useWindowSize } from 'hooks/ui';

interface Props {
	id?: string;
	data: PlotNumericColumnsV2;
	grouping: boolean;
	isLegendEnabled: boolean;
	legendHeader?: string;
	errorBar: AnalysisStatisticAggregationType;
	isForExport?: boolean;
	scalesLabels: ScalesLabels;
	loading: boolean;
	isConfigPanelOpen: boolean;
}

function Component({
	id,
	data,
	grouping,
	isLegendEnabled,
	legendHeader,
	errorBar,
	isForExport,
	scalesLabels,
	loading,
	isConfigPanelOpen
}: Props) {
	const { translate } = useTranslation();
	const { analysisId } = useAnalysisContext() as { analysisId: string };
	const [plots, togglePlot] = useToggleAnalysisChartPlot(analysisId);
	const [fullscreen] = useFullscreenAnalysis();
	const windowSize = useWindowSize();
	const [activeColumns] = useAnalysesActiveColum();
	const analysisIds = useAnalyses();
	const [{ filters }] = useFilters();
	const { series, options } = useComputeData(
		data,
		grouping,
		isLegendEnabled,
		scalesLabels,
		activeColumns,
		!!fullscreen,
		plots,
		errorBar,
		legendHeader
	);

	const renderId = useMemo(generate, [
		fullscreen,
		activeColumns,
		windowSize,
		analysisIds,
		filters,
		isConfigPanelOpen
	]);

	if (isEmpty(data) && !loading) {
		return (
			<AnalysisContainer>
				<NoPlot message={translate(({ analysis }) => analysis.errors.noResults)} />
			</AnalysisContainer>
		);
	}

	function onTogglePlot(e: ZingChartClickEvent) {
		if (isForExport) return;
		togglePlot({ id: analysisId, plotIndex: e.plotindex });
	}

	return (
		<Chart
			key={renderId}
			onLegendItemClick={onTogglePlot}
			id={id}
			type={t => t.Bar}
			series={series}
			options={options}
			effect={e => e.ExpandBottom}
			method={m => m.RegularEaseOut}
			isForExport={isForExport}
			height={'100%'}
		/>
	);
}

export const PlotNumericColumnsChartV2 = withMemo(Component);
